/**
 * The project (`world`) this hub page belongs to.
 *
 * The hub is multi-tenant by hostname: `GET /api/v2/worlds/settings?domainName=<host>`
 * maps the domain to exactly one project, and that mapping cannot change while
 * the page is open. The value is therefore resolved once and kept in the module
 * rather than in React state — the same shape as `setSessionId` /
 * `setMytaCorrelationId` in `src/http/axios.ts`.
 *
 * It is written twice from the two places that already read the settings
 * response: `resolveCognitoParams` during bootstrap (before the first render,
 * so consumers never see an unresolved value on a normal load) and
 * `getAppSettings` in `MytaverseProvider`, which heals the value if the
 * bootstrap request failed.
 *
 * **Need the current project? Call `getCurrentWorldId()`.** Do not re-resolve
 * it from the hostname and do not keep a second copy — `MytaverseProvider`
 * exposes `worldId` for React consumers and reads it from here too.
 */
let currentWorldId: string | undefined;

/**
 * Records the project the current host resolves to.
 *
 * @param worldId - Project id from `GET /api/v2/worlds/settings`. Omit it (or
 * pass `undefined`) to clear the value — the way a unit test resets the module
 * between cases.
 */
export const setCurrentWorldId = (worldId?: string): void => {
  currentWorldId = worldId;
};

/**
 * Reads the project the current host resolves to.
 *
 * @returns The project id, or `undefined` while the settings request has not
 * answered yet or answered without one (a legacy environment, or an offline
 * API on a locally served page).
 */
export const getCurrentWorldId = (): string | undefined => currentWorldId;
